import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Center,
  Flex,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { RouterOutputs } from '@duke-hq/svc-api/admin';
import { Trans } from '@lingui/react/macro';
import { RESOURCE_STATUSES } from '@duke-hq/shared-constants/resourceStatuses';
import { ResourceStatusBadge } from '@duke-hq/ui/components/ResourceStatusBadge/ResourceStatusBadge';
import { djs } from '@duke-hq/djs';
import { CalendarClock, ListTodo, MessagesSquare } from 'lucide-react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { StudentCourseEnrolmentStatus, StudentScheduleItemType } from '@duke-hq/svc-api-db';
import type { ProcessedUnits, ScheduleAdjustment, UnitActionsProps } from './types';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

export function UnitScheduleItem({
  unitSchedule,
  courseEnrolmentScheduleItems,
  UnitActionsComponent,
  courseEnrolmentId,
  enrolmentStatus,
  isAccredited,
  isWorkPlacement,
  isLLN,
  scheduleAdjustment,
  getAssessmentLink,
}: {
  unitSchedule: ProcessedUnits[string][number];
  courseEnrolmentScheduleItems: RouterOutputs['enrolment']['byIdWithResourceStatus']['courseEnrolmentScheduleItems'];
  UnitActionsComponent?: React.ComponentType<UnitActionsProps>;
  courseEnrolmentId: number;
  enrolmentStatus: StudentCourseEnrolmentStatus;
  isAccredited: boolean;
  isWorkPlacement: boolean;
  isLLN: boolean;
  scheduleAdjustment: ScheduleAdjustment | null;
  getAssessmentLink?: (submissionId: number) => string;
}): React.ReactElement {
  const unitWasAdjusted = scheduleAdjustment != null;
  const adjustedLabel = unitWasAdjusted
    ? `Adjusted on ${djs(scheduleAdjustment.updated_at).format('D MMM YYYY')}`
    : undefined;
  return (
    <AccordionItem key={unitSchedule.id} w="full">
      <AccordionButton as="div" w="full" cursor="pointer" p={0}>
        <Flex justifyContent="space-between" w="full">
          <Flex>
            <Flex w="9" alignItems="center" justifyContent="center" textAlign="center">
              <AccordionIcon />
            </Flex>
            <Box p={2}>
              <Flex flexDirection="column" whiteSpace="pre-wrap">
                <Text fontWeight="medium" fontSize="md" lineHeight="6">
                  {unitSchedule.unitTitle}
                </Text>
                <Flex alignItems="center" gap={2}>
                  <Text fontSize="md" fontWeight="normal" color="fg.muted">
                    {unitSchedule.unitCode}
                  </Text>
                  {isWorkPlacement ? (
                    <Badge
                      colorScheme="gray"
                      variant="subtle"
                      borderRadius="full"
                      textTransform="none"
                      fontWeight="medium"
                      fontSize="xs"
                      px={2}
                      whiteSpace="nowrap"
                    >
                      <Trans>Work placement</Trans>
                    </Badge>
                  ) : null}
                </Flex>
              </Flex>
            </Box>
          </Flex>
          <Flex>
            <Box p={2} w={pxToRem(120)} alignContent="center" color="fg.emphasis">
              <Flex alignItems="center" justifyContent="center" gap={1}>
                <Text fontSize="sm" textAlign="center">
                  {unitSchedule.effectiveStartDate != null
                    ? djs(unitSchedule.effectiveStartDate).format('D MMM YYYY')
                    : null}
                </Text>
                {unitWasAdjusted ? (
                  <Tooltip label={adjustedLabel} hasArrow placement="top">
                    <Box as="span" color="blue.500" cursor="pointer" aria-label={adjustedLabel}>
                      <CalendarClock size={14} />
                    </Box>
                  </Tooltip>
                ) : null}
              </Flex>
            </Box>
            <Box p={2} w={pxToRem(136)} alignContent="center">
              <Center>
                <ResourceStatusBadge
                  status={unitSchedule.overallStatus}
                  toolTipLabel={
                    unitSchedule.overallStatus === RESOURCE_STATUSES.PENDING ? (
                      <Trans>Pending work placement</Trans>
                    ) : undefined
                  }
                />
              </Center>
            </Box>
            {UnitActionsComponent ? (
              <Box p={2} alignContent="center" w="14">
                <UnitActionsComponent
                  enrolmentId={courseEnrolmentId}
                  unitOfCompetencyId={unitSchedule.unitOfCompetencyId}
                  previousItemDueDate={
                    courseEnrolmentScheduleItems[unitSchedule.position - 1]?.scheduled_start_date ??
                    ''
                  }
                  enrolmentStatus={enrolmentStatus}
                  unitScheduleOverallStatus={unitSchedule.overallStatus}
                  isAccredited={isAccredited}
                  isLLN={isLLN}
                />
              </Box>
            ) : null}
          </Flex>
        </Flex>
      </AccordionButton>

      <AccordionPanel w="100%" whiteSpace="pre-wrap" p={0} borderWidth={0}>
        {unitSchedule.scheduleItemDetails.map((scheduleItem) => {
          const submissionId =
            scheduleItem.item_type === StudentScheduleItemType.ASSESSMENT
              ? scheduleItem.submissionId
              : null;
          const assessmentLink =
            submissionId != null && getAssessmentLink != null
              ? getAssessmentLink(submissionId)
              : null;
          const isClickable = assessmentLink != null;

          const rowContent = (
            <>
              <Flex>
                <Box p={2} w="9"></Box>
                <Box p={2}>
                  <Flex gap={2} alignItems="center">
                    {scheduleItem.item_type === StudentScheduleItemType.ASSESSMENT ? (
                      <ListTodo />
                    ) : (
                      <MessagesSquare />
                    )}
                    <Text
                      fontWeight="medium"
                      fontSize="md"
                      lineHeight="6"
                      color={isClickable ? 'brand.500' : undefined}
                    >
                      {scheduleItem.itemTitle}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
              <Flex>
                <Box p={2} w={pxToRem(136)} alignContent={'center'}>
                  <Center>
                    <ResourceStatusBadge
                      status={scheduleItem.resourceStatus ?? RESOURCE_STATUSES.NOT_STARTED}
                    />
                  </Center>
                </Box>
                {UnitActionsComponent ? <Box w="14"></Box> : null}
              </Flex>
            </>
          );

          return isClickable ? (
            <Flex
              key={scheduleItem.id}
              as={RouterLink}
              to={assessmentLink}
              justifyContent="space-between"
              borderTopWidth={1}
              cursor="pointer"
              _hover={{ bg: 'bg.muted' }}
            >
              {rowContent}
            </Flex>
          ) : (
            <Flex key={scheduleItem.id} justifyContent="space-between" borderTopWidth={1}>
              {rowContent}
            </Flex>
          );
        })}
      </AccordionPanel>
    </AccordionItem>
  );
}
